import { getPhotoContentType } from './resizePhoto';

const MIN_PHOTO_SIZE = 256 * 1024;
const PROCESSING_TIMEOUT_MS = 10_000;

// Decode one full-resolution photo at a time, including during parallel uploads.
let processingQueue: Promise<void> = Promise.resolve();

const processInWorker = (file: File): Promise<File> =>
    new Promise((resolve) => {
        let worker: Worker | undefined;
        let finished = false;
        const timeout = setTimeout(() => finish(file), PROCESSING_TIMEOUT_MS);
        const finish = (result: File) => {
            if (finished) return;
            finished = true;
            clearTimeout(timeout);
            worker?.terminate();
            resolve(result);
        };
        // Inline workers work with CDN assets. Load the worker only for photo uploads,
        // with the same timeout covering both the download and image processing.
        import('./preprocessPhoto.worker?worker&inline')
            .then(({ default: PhotoWorker }) => {
                if (finished) return;
                try {
                    worker = new PhotoWorker();
                    worker.onerror = (event) => {
                        event.preventDefault();
                        finish(file);
                    };
                    worker.onmessageerror = () => finish(file);
                    worker.onmessage = ({ data }: MessageEvent<Blob | null>) => {
                        try {
                            if (
                                !(data instanceof Blob) ||
                                data.size === 0 ||
                                data.size >= file.size
                            ) {
                                finish(file);
                                return;
                            }
                            const extension = data.type === 'image/png' ? 'png' : 'jpg';
                            if (data.type !== 'image/png' && data.type !== 'image/jpeg') {
                                finish(file);
                                return;
                            }
                            finish(
                                new File(
                                    [data],
                                    `${file.name.replace(/\.[^.]+$/, '')}.${extension}`,
                                    {
                                        type: data.type,
                                        lastModified: file.lastModified,
                                    },
                                ),
                            );
                        } catch {
                            finish(file);
                        }
                    };
                    worker.postMessage(file);
                } catch {
                    finish(file);
                }
            })
            .catch(() => finish(file));
    });

export const preprocessPhoto = (file: File): Promise<File> => {
    if (file.size < MIN_PHOTO_SIZE || !getPhotoContentType(file) || typeof Worker !== 'function') {
        return Promise.resolve(file);
    }
    const result = processingQueue.then(() => processInWorker(file)).catch(() => file);
    processingQueue = result.then(() => {});
    return result;
};
